import React, { useRef } from "react";
import { Button, Space, Modal, Alert, Typography } from "antd";
import {
  ExportOutlined,
  ImportOutlined,
  FileExcelOutlined,
  UploadOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

export interface ExcelImportExportProps {
  onExport: () => void;
  onImport: (file: File) => void;
  onDownloadTemplate: () => void;
  importLoading: boolean;
  importModalVisible: boolean;
  setImportModalVisible: (visible: boolean) => void;
  hasData: boolean;
  disabled?: boolean;
}

export const ExcelImportExportButtons: React.FC<
  Pick<
    ExcelImportExportProps,
    | "onExport"
    | "onDownloadTemplate"
    | "setImportModalVisible"
    | "hasData"
    | "disabled"
  >
> = ({
  onExport,
  onDownloadTemplate,
  setImportModalVisible,
  hasData,
  disabled = false,
}) => (
  <Space>
    {/* <Button
      icon={<FileExcelOutlined />}
      onClick={onDownloadTemplate}
      disabled={disabled}
    >
      Download Template
    </Button> */}
    <Button
      icon={<ImportOutlined />}
      onClick={() => setImportModalVisible(true)}
      disabled={disabled}
    >
      Import Take-off
    </Button>
    {/* <Button
      icon={<ExportOutlined />}
      onClick={onExport}
      disabled={!hasData || disabled}
    >
      Export Excel
    </Button> */}
  </Space>
);

export const ExcelImportModal: React.FC<
  Pick<
    ExcelImportExportProps,
    | "onImport"
    | "onDownloadTemplate"
    | "importLoading"
    | "importModalVisible"
    | "setImportModalVisible"
  >
> = ({
  onImport,
  onDownloadTemplate,
  importLoading,
  importModalVisible,
  setImportModalVisible,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImport = (file: File) => {
    onImport(file);
    // Reset the input value to allow importing the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleModalClose = () => {
    // Reset file input when modal is closed
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    setImportModalVisible(false);
  };

  return (
    <Modal
      title="Import Data from Excel"
      open={importModalVisible}
      onCancel={handleModalClose}
      footer={null}
      width={600}
    >
      <div className="space-y-4">
        <Alert
          message="Import Instructions"
          description={
            <div>
              <p>
                1. Download the template Excel file using the "Download
                Template" button
              </p>
              <p>2. Fill in your data following the template format</p>
              <p>3. Upload the completed Excel file below</p>
              <p>
                <strong>Note:</strong> Calculated fields will be automatically
                computed after import
              </p>
            </div>
          }
          type="info"
          showIcon
          className="mb-4"
        />

        <div
          className={`border-2 border-dashed border-gray-300 rounded-lg p-6 text-center ${
            importLoading ? "cursor-not-allowed" : "cursor-pointer"
          }`}
          onClick={() => {
            if (!importLoading) {
              fileInputRef.current?.click();
            }
          }}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleImport(file);
              }
            }}
            style={{ display: "none" }}
            id="excel-upload-input"
            disabled={importLoading}
          />
          <UploadOutlined className="text-4xl text-gray-400 mb-4" />
          <div className="text-lg font-medium">
            {importLoading ? "Processing..." : "Click to upload Excel file"}
          </div>
          <div className="text-gray-500">or drag and drop</div>
          <div className="text-sm text-gray-400 mt-2">
            Supports .xlsx and .xls files
          </div>
        </div>

        {importLoading && (
          <div className="text-center py-4">
            <Text>Processing Excel file...</Text>
          </div>
        )}

        <div className="flex justify-end">
          {/* <Button
          icon={<FileExcelOutlined />}
          onClick={onDownloadTemplate}
          disabled={importLoading}
        >
          Download Template
        </Button> */}
          <Button onClick={handleModalClose} disabled={importLoading}>
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export const ExcelImportExport: React.FC<ExcelImportExportProps> = (props) => (
  <>
    <ExcelImportExportButtons {...props} />
    <ExcelImportModal {...props} />
  </>
);

export default ExcelImportExport;
